import { type FC } from 'react';
import { Button, Input, useAlert } from '@selfinspectionorg/ui-kit';
import styles from './ApiTab.module.scss';
import CopyIcon from './assets/copy.svg?react';
import { SectionHeading } from '@/modules/profile/components';

interface Props {
  /** The account's API access token, sourced from the profile view (`IUserProfileView.apiKey`). */
  token: string;
}

/**
 * The API tab: an "Access token" section showing the account's read-only "Default API token" with a
 * copy-to-clipboard action. The token is never editable from this page.
 */
export const ApiTab: FC<Props> = ({ token }) => {
  const alert = useAlert();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      alert.success('Token copied to clipboard', undefined, {
        showCloseButton: true,
        showIcon: true,
        testId: 'api-token-copied',
      });
    } catch {
      // Clipboard access can be denied; surface it rather than silently claiming success.
      alert.error("Couldn't copy the token", undefined, { showCloseButton: true, showIcon: true });
    }
  };

  return (
    <section className={styles.section}>
      <SectionHeading title="Access token" />
      <Input
        name="apiToken"
        label="Default API token"
        placeholder="API token"
        value={token}
        readOnly
        testId="api-token-input"
        addonAfter={
          <Button.Ghost
            onClick={copy}
            disabled={!token}
            aria-label="Copy token"
            className={styles.copyButton}
            data-testid="copy-api-token-button"
          >
            <CopyIcon />
          </Button.Ghost>
        }
      />
    </section>
  );
};
